import type { Editor } from "@tiptap/react";
import { ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";

export type BlockType = "p" | "h1" | "h2" | "h3" | "h4" | "h5" | "h6" | "code";
type HeadingLevel = 1 | 2 | 3 | 4 | 5 | 6;

const OPTIONS: { value: BlockType; label: string; preview: string }[] = [
  { value: "p", label: "Paragraf", preview: "text-sm" },
  { value: "h1", label: "Başlık 1", preview: "text-2xl font-extrabold" },
  { value: "h2", label: "Başlık 2", preview: "text-xl font-bold" },
  { value: "h3", label: "Başlık 3", preview: "text-lg font-bold" },
  { value: "h4", label: "Başlık 4", preview: "text-base font-bold" },
  { value: "h5", label: "Başlık 5", preview: "text-sm font-bold" },
  { value: "h6", label: "Başlık 6", preview: "text-xs font-bold uppercase" },
  { value: "code", label: "Kod bloğu", preview: "font-mono text-xs" },
];

function apply(editor: Editor, type: BlockType) {
  const chain = editor.chain().focus();
  if (type === "p") chain.setParagraph().run();
  else if (type === "code") chain.setCodeBlock().run();
  else chain.setHeading({ level: Number(type.slice(1)) as HeadingLevel }).run();
}

// Paragraf / Başlık 1–6 / Kod bloğu seçimi.
export function BlockTypeMenu({ editor, value }: { editor: Editor; value: BlockType }) {
  const current = OPTIONS.find((o) => o.value === value) ?? OPTIONS[0];
  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          className="h-7 w-[6.75rem] justify-between px-2 text-xs font-normal"
          title="Metin biçimi"
          aria-label={`Metin biçimi: ${current.label}`}
        >
          {current.label}
          <ChevronDown className="size-3.5 opacity-60" />
        </Button>
      </DropdownMenuTrigger>
      {/* Menü kapanınca odak tetikleyiciye dönmesin; komut editöre odaklanıyor. */}
      <DropdownMenuContent align="start" onCloseAutoFocus={(e) => e.preventDefault()}>
        {OPTIONS.map((o) => (
          <DropdownMenuItem
            key={o.value}
            onSelect={() => apply(editor, o.value)}
            className={cn(o.value === value && "bg-accent")}
          >
            <span className={o.preview}>{o.label}</span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
